import type { Currency } from '../../../contexts/CurrencyContext'
import type { InventoryProduct } from './inventoryTypes'

const MS_PER_DAY = 24 * 60 * 60 * 1000

function formatShortDate(isoString: string): string | null {
  const date = new Date(isoString)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(date)
}

export const getSaleTimingLabel = (
  product: Pick<InventoryProduct, 'onSale' | 'saleStartAt' | 'saleEndAt' | 'saleInfo'>,
): string | null => {
  if (!product.onSale) return null

  const startAt = product.saleStartAt ?? product.saleInfo?.saleStartAt ?? null
  const endAt = product.saleEndAt ?? product.saleInfo?.saleEndAt ?? null

  if (startAt) {
    const startDate = new Date(startAt)
    if (!Number.isNaN(startDate.getTime()) && startDate.getTime() > Date.now()) {
      const startText = formatShortDate(startAt)
      return startText ? `Starts ${startText}` : 'Starts soon'
    }
  }

  if (!endAt) return 'No end'

  const endDate = new Date(endAt)
  if (Number.isNaN(endDate.getTime())) return null

  const endText = formatShortDate(endAt)
  const msRemaining = endDate.getTime() - Date.now()
  const daysRemaining = Math.ceil(msRemaining / MS_PER_DAY)

  if (!endText) return daysRemaining > 0 ? `Ends in ${daysRemaining}d` : 'Ends today'
  if (daysRemaining <= 0) return `Ends ${endText}`
  return `Ends ${endText} (${daysRemaining}d)`
}

export const getManualPriceTimingLabel = (
  product: Pick<InventoryProduct, 'manualPriceCents' | 'manualPriceExpiresAt'>,
): string | null => {
  if (!product.manualPriceCents) return null
  if (!product.manualPriceExpiresAt) return 'Forever'

  const expiresAt = new Date(product.manualPriceExpiresAt)
  if (Number.isNaN(expiresAt.getTime())) return null

  const now = Date.now()
  if (expiresAt.getTime() <= now) return 'Expired'

  const endText = formatShortDate(product.manualPriceExpiresAt)
  const msRemaining = expiresAt.getTime() - now
  const daysRemaining = Math.ceil(msRemaining / MS_PER_DAY)

  if (!endText) return daysRemaining > 0 ? `Ends in ${daysRemaining}d` : 'Ends today'
  if (daysRemaining <= 0) return `Ends ${endText}`
  return `Ends ${endText} (${daysRemaining}d)`
}

export const isManualPriceActive = (product: Pick<InventoryProduct, 'manualPriceCents' | 'manualPriceExpiresAt'>): boolean => {
  if (product.manualPriceCents === null || product.manualPriceCents === undefined) return false
  if (!product.manualPriceExpiresAt) return true
  const expiresAt = new Date(product.manualPriceExpiresAt)
  if (Number.isNaN(expiresAt.getTime())) return true
  return expiresAt.getTime() > Date.now()
}

export const getSaleConfig = (
  product: Pick<InventoryProduct, 'saleType' | 'saleValue' | 'saleInfo'>,
): {
  saleType?: 'percentage' | 'fixed'
  saleValue?: number
} => ({
  saleType: (product.saleType ?? product.saleInfo?.saleType ?? undefined) as 'percentage' | 'fixed' | undefined,
  saleValue: (product.saleValue ?? product.saleInfo?.saleValue ?? undefined) as number | undefined,
})

export const isSaleConfigured = (
  product: Pick<InventoryProduct, 'onSale' | 'saleType' | 'saleValue' | 'saleInfo'>,
): boolean => {
  const { saleType, saleValue } = getSaleConfig(product)
  return Boolean(product.onSale && saleType && typeof saleValue === 'number' && saleValue > 0)
}

export const isSaleActiveNow = (
  product: Pick<
    InventoryProduct,
    'onSale' | 'saleType' | 'saleValue' | 'saleStartAt' | 'saleEndAt' | 'saleInfo'
  >,
): boolean => {
  if (!isSaleConfigured(product)) return false

  const startAt = product.saleStartAt ?? product.saleInfo?.saleStartAt ?? null
  const endAt = product.saleEndAt ?? product.saleInfo?.saleEndAt ?? null

  const now = Date.now()

  if (startAt) {
    const startDate = new Date(startAt)
    if (!Number.isNaN(startDate.getTime()) && startDate.getTime() > now) return false
  }

  if (endAt) {
    const endDate = new Date(endAt)
    if (!Number.isNaN(endDate.getTime()) && endDate.getTime() <= now) return false
  }

  return true
}

export const normalizePriceCents = (value: unknown): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null
  return Math.max(0, Math.round(value))
}

export type InventoryPricingContext = {
  formatPrice: (cents: number, fromCurrency?: Currency) => string
}

/**
 * Format a server-resolved store-currency amount (`listedPrice` / `storePrices`). It is passed
 * WITH its currency, so `formatPrice` shows it verbatim when the viewer looks in the store's
 * currency (the ordinary case) — a typed A$1,100.00 reads A$1,100.00. A missing amount or
 * currency means the server could not price it: say so, never substitute a conversion.
 */
export const formatStoreAmount = (
  formatPrice: InventoryPricingContext['formatPrice'],
  cents: number | null | undefined,
  currency: string | null | undefined,
  unavailableLabel: string,
): string => {
  if (typeof cents !== 'number' || !Number.isFinite(cents) || !currency) return unavailableLabel
  return formatPrice(cents, currency as Currency)
}
